import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Business,
  Save,
  CameraAlt,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { User, getCurrentUser, updateUser } from '../../store/slices/authSlice';
import apiService from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth.user);
  const authLoading = useAppSelector(state => state.auth.loading);
  const [loading, setLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    currentPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Remove auto-refresh to prevent infinite loop
  // useEffect(() => {
  //   if (!user && !authLoading) {
  //     dispatch(getCurrentUser());
  //   }
  // }, [dispatch, user, authLoading]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        confirmPassword: '',
        currentPassword: '',
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAvatarPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Upload the image immediately
      try {
        const formData = new FormData();
        formData.append('avatar', file);
        
        const response = await apiService.uploadFile(`/api/users/${user?.id}/avatar`, formData);
        
        toast.success('Profile picture updated successfully!');
        
        // Update user data with new avatar
        if (response.data) {
          dispatch(updateUser(response.data));
        }
      } catch (error: any) {
        console.error('Error uploading avatar:', error);
        toast.error(error.message || 'Failed to upload profile picture');
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.password && !formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required to change password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const profileData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        ...(formData.password && {
          password: formData.password,
          current_password: formData.currentPassword,
        }),
      };

      const response = await apiService.put(`/api/users/${user?.id}`, profileData);
      
      toast.success('Profile updated successfully!');
      
      // Update user data directly in Redux state with the response data
      if (response.data) {
        dispatch(updateUser(response.data));
      }
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: '',
        currentPassword: '',
      }));
    } catch (error: any) {
      console.error('Error updating profile:', error);
      
      if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        const newErrors: Record<string, string> = {};
        
        Object.keys(serverErrors).forEach(key => {
          newErrors[key] = serverErrors[key][0];
        });
        
        setErrors(newErrors);
      } else {
        toast.error(error.message || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        confirmPassword: '',
        currentPassword: '',
      });
    }
    setErrors({});
    setAvatarPreview(null);
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'inactive': return '#6b7280';
      case 'suspended': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Show loading if auth is loading
  if (authLoading) {
    return (
      <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: '#6b7280' }}>
          Loading profile data...
        </Typography>
      </Box>
    );
  }

  // Show message if user is not available
  if (!user) {
    return (
      <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: '#6b7280' }}>
          No user data available. Please log in again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      <ToastContainer />
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{
            borderRadius: '8px',
            borderColor: '#d1d5db',
            color: '#374151',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Back
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Main Content */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Card sx={{ height: 'calc(100vh - 140px)', borderRadius: '12px' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Form Header */}
              <Box sx={{ 
                p: 3, 
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#374151' }}>
                  Profile Settings
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      console.log('Manual getCurrentUser call');
                      dispatch(getCurrentUser());
                    }}
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:hover': {
                        borderColor: '#9ca3af',
                        backgroundColor: '#f9fafb',
                      },
                    }}
                  >
                    Test API Call
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Clear localStorage and force fresh login
                      localStorage.removeItem('user');
                      localStorage.removeItem('token');
                      window.location.href = '/login';
                    }}
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:hover': {
                        borderColor: '#9ca3af',
                        backgroundColor: '#f9fafb',
                      },
                    }}
                  >
                    Force Fresh Login
                  </Button>
                </Box>
              </Box>

              {/* Form Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                <Grid container spacing={3}>
                  {/* Profile Picture Section */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                        Profile Picture
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Avatar
                          src={avatarPreview || user?.avatar}
                          sx={{ 
                            width: 100, 
                            height: 100, 
                            fontSize: '2rem',
                            backgroundColor: '#4A90E2'
                          }}
                        >
                          <Person sx={{ fontSize: '3rem' }} />
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                              {formData.name || 'User Name'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                backgroundColor: getStatusColor(user?.status || 'active') 
                              }} />
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {user?.status || 'active'}
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            variant="outlined"
                            startIcon={<CameraAlt />}
                            component="label"
                            sx={{
                              borderRadius: '8px',
                              borderColor: '#d1d5db',
                              color: '#374151',
                              '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb',
                              },
                            }}
                          >
                            Change Photo
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleAvatarChange}
                            />
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Personal Information */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                        Personal Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            name="name"
                            label="Full Name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            name="email"
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            name="phone"
                            label="Phone Number"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Security Settings */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                        Security Settings
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            name="currentPassword"
                            label="Current Password"
                            type="password"
                            value={formData.currentPassword}
                            onChange={handleInputChange}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword}
                            autoComplete="new-password"
                            inputProps={{ autoComplete: "off" }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            name="password"
                            label="New Password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            autoComplete="new-password"
                            inputProps={{ autoComplete: "off" }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            name="confirmPassword"
                            label="Confirm New Password"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            autoComplete="new-password"
                            inputProps={{ autoComplete: "off" }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Form Footer */}
              <Box sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: '1px solid #e5e7eb',
                backgroundColor: '#f8fafc',
                borderRadius: '0 0 12px 12px',
                margin: '0 -24px -24px -24px',
                padding: '24px 48px'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      px: 4,
                      py: 2,
                      borderRadius: '8px',
                      borderColor: '#d1d5db',
                      color: '#374151',
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      minWidth: '140px',
                      '&:hover': {
                        borderColor: '#9ca3af',
                        backgroundColor: '#f9fafb',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={handleSubmit}
                    sx={{
                      px: 4,
                      py: 2,
                      borderRadius: '8px',
                      backgroundColor: '#4A90E2',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      minWidth: '140px',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
                      },
                      '&:disabled': {
                        backgroundColor: '#9ca3af',
                        color: 'white',
                      },
                    }}
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
